/* ══════════════════════════════
   COMPLETE — memory sequence game.
══════════════════════════════ */

import { setupNavigation, cleanupNavigation } from '../UI/navigation.js';
import { langManager } from '../lang/langManager.js';
import {
  awardMedal,
  renderTitleMedals,
  medalModalHTML,
  getNavV,
  getNavH,
  getGameSoundEnabled,
  setGameSoundEnabled
} from '../utils/utilsJeux.js';
import {
  initJeuxModal,
  openJeuxModal,
  closeJeuxModal,
  cleanupJeuxModal
} from '../utils/utilsJeux.js';
import { log } from '../utils/logging.js';

// ── PAGE ROUTING ───────────────────────────────────────────────
const pageRouteMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games',
  boutique: '/boutique'
};

const onNavigateHome = () => {
  window.location.href = pageRouteMap.home;
};
window.addEventListener('navigate-home', onNavigateHome);

const onNavigateSection = e => {
  const target = pageRouteMap[e.detail?.section];
  if (target) window.location.href = target;
};
window.addEventListener('navigate-section', onNavigateSection);

setupNavigation();

/* ══════════════════════════════
   COMPLETE — main game logic
══════════════════════════════ */

const t = key => langManager.getText(key);

// ── Chrome height measurement ──────────────────────────────────
function updateSimonChromeH() {
  const title = document.querySelector('.page-title');
  const scorebar = document.querySelector('.scorebar');
  const status = document.querySelector('#simon-status');
  const msgEl_ = document.querySelector('.msg');
  const btn = document.querySelector('#simon-start-btn');
  const gameDiv = document.querySelector('.game-div');
  const mainEl = document.querySelector('main');

  if (!gameDiv || !mainEl) return;

  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  const msgH = msgEl_ ? msgEl_.offsetHeight : 0;

  const gameDivGap = parseFloat(getComputedStyle(gameDiv).gap) || 12;
  const mainStyle = getComputedStyle(mainEl);
  const mainPadT = parseFloat(mainStyle.paddingTop);
  const mainPadB = parseFloat(mainStyle.paddingBottom);
  const mainPadV = mainPadT + mainPadB;

  const titleH = title ? title.offsetHeight : 0;
  const scorebarH = scorebar ? scorebar.offsetHeight : 0;
  const statusH = status ? status.offsetHeight : 0;
  const btnH = btn ? btn.offsetHeight : 0;

  const items = [title, scorebar, status, msgEl_, btn].filter(Boolean);
  const itemsTotalH = titleH + scorebarH + statusH + msgH + btnH;
  const gapsTotalH = gameDivGap * Math.max(0, items.length - 1);
  const navOffset = getNavV();
  const navHoriz = getNavH();

  const chromeItems = itemsTotalH + gapsTotalH;
  const mainAvailH = mainEl.clientHeight - mainPadV;
  const availH = mainAvailH - chromeItems;

  const chromeTotal = chromeItems + mainPadV + navOffset; // for --simon-chrome-h CSS var
  document.documentElement.style.setProperty('--simon-chrome-h', `${chromeTotal}px`);

  console.groupCollapsed(
    '%c[simon resize]',
    'color:#1d7a1d;font-weight:bold',
    `avail h ${Math.round(availH)}  (main ${mainEl.clientHeight} - padV ${Math.round(mainPadV)} - items ${Math.round(chromeItems)})`
  );
  log('  window              ', vpW, '×', vpH);
  log('  h-navbar            ', navOffset, '   sidebar-w:', navHoriz);
  log('  main.clientH        ', mainEl.clientHeight, '   padT:', mainPadT, 'padB:', mainPadB);
  log('  mainAvailH          ', Math.round(mainAvailH));
  log(
    '  game-div gap        ',
    gameDivGap,
    '× (',
    items.length - 1,
    'gaps) =',
    Math.round(gapsTotalH)
  );
  log('  title               ', titleH);
  log('  scorebar            ', scorebarH);
  log('  status              ', statusH);
  log('  msg                 ', msgH);
  log('  btn                 ', btnH);
  log('  ▶ items+gaps        ', Math.round(chromeItems));
  log('  ▶ avail h           ', Math.round(availH));
  console.groupEnd();
}

let _simonResizeTimer = null;
requestAnimationFrame(() => requestAnimationFrame(updateSimonChromeH));

const onSimonResize = () => {
  clearTimeout(_simonResizeTimer);
  _simonResizeTimer = setTimeout(updateSimonChromeH, 100);
};
window.addEventListener('resize', onSimonResize);

// ── Sound toggle ───────────────────────────────────────────────
let simonSoundEnabled = getGameSoundEnabled();

function initSoundToggle() {
  const btn = document.getElementById('simon-sound-toggle');
  if (!btn) return;

  function update() {
    btn.setAttribute('aria-pressed', String(simonSoundEnabled));
    btn.classList.toggle('muted', !simonSoundEnabled);
    btn.title = simonSoundEnabled ? t('soundOff') : t('soundOn');
  }

  btn.addEventListener('click', () => {
    simonSoundEnabled = !simonSoundEnabled;
    setGameSoundEnabled(simonSoundEnabled);
    update();
  });

  update();
}

// ── Image display ──────────────────────────────────────────────
const SIMON_DIRS = ['top', 'right', 'left', 'bottom'];

function showImage(name) {
  const grid = document.getElementById('simon-grid');
  grid.classList.remove('show-close', 'show-top', 'show-bottom', 'show-left', 'show-right');
  grid.classList.add(`show-${name}`);
}

// ── Audio ──────────────────────────────────────────────────────
const SIMON_FREQS = [174.61, 130.81, 196.0, 261.63];
let simonAudioCtx = null;

function getAudioCtx() {
  if (!simonAudioCtx) simonAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return simonAudioCtx;
}

function playTone(idx, dur = 600) {
  if (!simonSoundEnabled) return;
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = SIMON_FREQS[idx];
    osc.type = 'square';
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur / 1000);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + dur / 1000);
  } catch (_) { }
}

// ── Game state ─────────────────────────────────────────────────
let simonSeq = [],
  simonInput = [],
  simonPlaying = false;
let simonBest = 0,
  simonAborted = false;

const sleep = ms => new Promise(r => setTimeout(r, ms));

// ── Button flash ───────────────────────────────────────────────
function litBtn(idx, dur = 600) {
  showImage(SIMON_DIRS[idx]);
  playTone(idx, dur);
  setTimeout(() => showImage('close'), dur);
}

// ── Dots ───────────────────────────────────────────────────────
function updateSimonDots() {
  const status = document.getElementById('simon-status');
  status.innerHTML = '';
  for (let i = 0; i < simonSeq.length; i++) {
    const dot = document.createElement('div');
    const cls = i < simonInput.length ? ' done' : i === simonInput.length ? ' active' : '';
    dot.className = 'simon-dot' + cls;
    status.appendChild(dot);
  }
}

// ── Game flow ──────────────────────────────────────────────────
async function startSimon() {
  simonAborted = true;
  await sleep(50);
  simonAborted = false;

  simonSeq = [];
  simonInput = [];
  simonPlaying = true;

  showImage('close');

  const startBtn = document.getElementById('simon-start-btn');
  startBtn.disabled = true;
  startBtn.textContent = t('start');

  document.getElementById('simon-status').innerHTML = '';
  document.getElementById('simon-level').textContent = '—';

  const msg = document.getElementById('simon-msg');
  msg.textContent = t('simonWatch');
  msg.className = 'msg info';

  await addSimonStep();
}

async function addSimonStep() {
  if (simonAborted) return;

  simonSeq.push(Math.floor(Math.random() * 4));
  simonInput = [];

  document.getElementById('simon-level').textContent = simonSeq.length;
  updateSimonDots();

  const msg = document.getElementById('simon-msg');
  msg.textContent = t('simonWatch');
  msg.className = 'msg info';

  await sleep(650);
  if (simonAborted) return;

  const speed = Math.max(280, 580 - simonSeq.length * 18);
  for (const idx of simonSeq) {
    if (simonAborted) return;
    litBtn(idx, speed - 60);
    await sleep(speed + 80);
  }

  if (simonAborted) return;
  showImage('close');
  msg.textContent = t('simonYourTurn');
  msg.className = 'msg';
  simonPlaying = false;
}

async function simonPress(idx) {
  if (simonPlaying || !simonSeq.length) return;
  simonPlaying = true;

  showImage(SIMON_DIRS[idx]);
  playTone(idx, 380);
  setTimeout(() => showImage('close'), 380);

  simonInput.push(idx);
  updateSimonDots();

  const pos = simonInput.length - 1;

  // ── Wrong ────────────────────────────────────────────────────
  if (simonInput[pos] !== simonSeq[pos]) {
    if (simonSeq.length > simonBest) {
      simonBest = simonSeq.length;
      document.getElementById('simon-best').textContent = simonBest;
    }

    const reached = simonSeq.length;
    simonSeq = [];
    simonInput = [];
    document.getElementById('simon-level').textContent = '—';
    document.getElementById('simon-status').innerHTML = '';

    const msg = document.getElementById('simon-msg');
    msg.textContent = t('simonPrompt');
    msg.className = 'msg';

    const earnedMedal = reached >= 17 ? 2 : reached >= 15 ? 3 : null;
    const bestMedal = awardMedal('simon', earnedMedal);
    renderTitleMedals('simon');

    document.getElementById('jeux-modal-body').innerHTML = `
      <div class="end-title">${t('gameOver')}</div>
      <div class="end-stats">
        <div class="end-stat">
          <div class="end-stat-val">${reached}</div>
          <div class="end-stat-lbl">${t('simonLevelReached')}</div>
        </div>
        <div class="end-stat">
          <div class="end-stat-val">${simonBest}</div>
          <div class="end-stat-lbl">${t('simonBestLevel')}</div>
        </div>
      </div>
      ${medalModalHTML(earnedMedal, bestMedal, t)}`;

    const startBtn = document.getElementById('simon-start-btn');
    startBtn.disabled = false;
    startBtn.textContent = t('start');
    openJeuxModal();
    return;
  }

  // ── Correct — sequence complete ──────────────────────────────
  if (simonInput.length === simonSeq.length) {
    const msg = document.getElementById('simon-msg');
    msg.textContent = t('simonCorrect');
    msg.className = 'msg ok';
    await sleep(750);
    if (simonAborted) return;

    // Win at level 20
    if (simonSeq.length >= 20) {
      const earnedMedal = 1;
      const bestMedal = awardMedal('simon', earnedMedal);
      renderTitleMedals('simon');

      document.getElementById('jeux-modal-body').innerHTML = `
        <div class="end-title">${t('youWin')} 🏆</div>
        <div class="end-rating">${t('simonMaster')}</div>
        <div class="end-stats">
          <div class="end-stat">
            <div class="end-stat-val">${simonSeq.length}</div>
            <div class="end-stat-lbl">${t('simonLevelReached')}</div>
          </div>
          <div class="end-stat">
            <div class="end-stat-val">${simonBest}</div>
            <div class="end-stat-lbl">${t('simonBestLevel')}</div>
          </div>
        </div>
        ${medalModalHTML(earnedMedal, bestMedal, t)}`;

      const startBtn = document.getElementById('simon-start-btn');
      startBtn.disabled = false;
      startBtn.textContent = t('start');
      msg.textContent = t('simonPrompt');
      msg.className = 'msg';
      openJeuxModal();
      return;
    }

    await addSimonStep();
  } else {
    simonPlaying = false;
  }
}

// ── Start button ───────────────────────────────────────────────
const startBtn = document.getElementById('simon-start-btn');
if (startBtn) {
  startBtn.textContent = t('start');
  startBtn.disabled = false;
  startBtn.addEventListener('click', () => {
    closeJeuxModal();
    startSimon();
  });
}

initSoundToggle();
renderTitleMedals('simon');
initJeuxModal();

window.simonPress = simonPress;

// ── Keyboard support ───────────────────────────────────────────
const SIMON_KEY_MAP = {
  ArrowUp: 0,
  ArrowRight: 1,
  ArrowLeft: 2,
  ArrowDown: 3
};

const onKeydown = e => {
  if (!(e.key in SIMON_KEY_MAP)) return;
  e.preventDefault();
  simonPress(SIMON_KEY_MAP[e.key]);
};

document.addEventListener('keydown', onKeydown);

// ── Cleanup on page hide ───────────────────────────────────────
window.addEventListener(
  'pagehide',
  () => {
    simonAborted = true;
    simonAudioCtx?.close();
    simonAudioCtx = null;

    window.removeEventListener('navigate-home', onNavigateHome);
    window.removeEventListener('navigate-section', onNavigateSection);
    window.removeEventListener('resize', onSimonResize);
    document.removeEventListener('keydown', onKeydown);

    delete window.simonPress;
    cleanupNavigation();
    cleanupJeuxModal();
  },
  { once: true }
);
